import { useState } from 'react';
import "./css/homepage_help.css";
import "./css/privacy.css";
import logo from "./images/logo_ticket_buydeal.jpg";
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('privacy');

  return (
    <div className="mainhelp-container">

      {/* NAVBAR */}
      <header className="navbar">
        <div className="navbar-inner">
          <div className="logo" onClick={() => navigate("/")}>
            <img src={logo} alt="" />
            <span>Buydeal.EU</span>
          </div>
          <div className="nav-buttons">
            <div className="nav-menu">
              <span className="nav-link" onClick={() => navigate("/orginfo")}>Event Organisers</span>
            </div>
            <div className="nav-menu">
              <span className="nav-link" onClick={() => navigate("/homepagehelp")}>Help</span>
            </div>
            <button className="btn-primary" onClick={() => navigate("/login")}>
              Organiser Login
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="mainhelp">
        <div className="mainhelp-text">
          <h1>Privacy &amp; Cookie Policy</h1>
          <p>Buydeal.eu B.V. · Utrecht, Netherlands · Last updated: June 2026</p>
        </div>
      </section>

      <div className="privacy-body">

        <div className="privacy-tabs">
          <button
            className={`privacy-tab${activeTab === 'privacy' ? ' active' : ''}`}
            onClick={() => setActiveTab('privacy')}
          >
            Privacy Policy
          </button>
          <button
            className={`privacy-tab${activeTab === 'cookies' ? ' active' : ''}`}
            onClick={() => setActiveTab('cookies')}
          >
            Cookie Policy
          </button>
        </div>

        {/* ── PRIVACY ── */}
        {activeTab === 'privacy' && (
          <div className="privacy-content">

            <div className="privacy-intro-box">
              At Buydeal, we take your privacy seriously. This page explains how we handle
              your personal data when you buy tickets, organise events, or simply browse our
              platform. We comply with the EU General Data Protection Regulation (GDPR) and
              the Dutch Uitvoeringswet AVG (UAVG). If you have any questions, reach us at{' '}
              <a href="mailto:privacy@buydeal.eu">admin@buydeal.eu</a>.
            </div>

            <Block title="About Buydeal">
              <p>
                Buydeal.eu B.V. is an online webshop for prepaid cards, cash cards, vouchers and event ticketing platform based in Utrecht,
                Netherlands. We connect event-goers with ticket buyers, and provide organisers
                with the tools to sell and manage their events. For the purposes of GDPR,
                Buydeal is the data controller for all personal data described in this policy,
                unless we indicate otherwise.
              </p>
            </Block>

            <Block title="What information we collect">
              <p>
                The personal data we collect depends on how you interact with Buydeal.
                When you purchase a ticket we ask for your name and email address so we can
                send your e-ticket and keep you informed about the event. If payment details
                are needed, they are handled securely by our payment provider — we never
                store your full card number.
              </p>
              <p>
                If you sign up as an event organiser, we also collect your company name,
                VAT number, and IBAN to process payouts and issue invoices. For all users,
                we automatically receive technical information such as your IP address,
                browser type, and device when you use our website — this helps us keep
                the platform secure and running smoothly.
              </p>
              <p>
                We do not collect or store sensitive personal data such as health information,
                racial or ethnic origin, religious beliefs, or political opinions.
              </p>
            </Block>

            <Block title="Why we use your data">
              <p>
                We only use your personal data for clear, legitimate reasons:
              </p>
              <ul>
                <li>
                  <strong>To fulfil your order</strong> — processing your ticket purchase,
                  sending confirmation emails and e-tickets, and enabling event entry via QR code.
                </li>
                <li>
                  <strong>To run our platform</strong> — managing organiser accounts, handling
                  payouts, generating invoices, and providing customer support.
                </li>
                <li>
                  <strong>To meet legal requirements</strong> — Dutch tax law requires us to
                  retain financial records for seven years (Belastingdienst obligation).
                </li>
                <li>
                  <strong>To improve our service</strong> — analysing anonymised usage patterns
                  to understand what works and what we can make better.
                </li>
                <li>
                  <strong>To keep you informed</strong> — if you opt in, we may occasionally
                  send you updates about events or platform features. You can unsubscribe at
                  any time via the link in any email.
                </li>
              </ul>
            </Block>

            {/*<Block title="A note on ticket personalisation">
              <p>
                Some event organisers ask you to personalise your ticket with additional
                information — for example your full name or date of birth — to comply with
                their entry requirements. In those cases, the organiser is the data controller
                for that specific data, not Buydeal. We recommend checking the organiser's
                own privacy notice for details on how they handle it.
              </p>
            </Block>*/}

            <Block title="How long we keep your data">
              <p>
                We keep your data only for as long as needed. Ticket purchase records and
                invoices are retained for seven years to meet Dutch fiscal requirements.
                Organiser account data is kept until the account is closed, plus one additional
                year. Buyer data related to a specific event is removed two years after the
                event date. Server and access logs are deleted after 90 days.
              </p>
            </Block>

            <Block title="Who we share your data with">
              <p>
                We do not sell your personal data. We share it only where necessary to
                deliver our service:
              </p>
              <ul>
                <li>
                  <strong>Event organisers</strong> receive your name and email to manage
                  attendance and communicate event information.
                </li>
                <li>
                  <strong>Payment providers</strong> such as CM and iDEAL process your
                  payment. They are PCI-DSS certified and handle payment data independently
                  under their own privacy policies.
                </li>
                <li>
                  <strong>Infrastructure and tools</strong> — we use Google cloud hosting, zoho email
                  delivery, and google analytics services. All providers are carefully selected and
                  bound by data processing agreements.
                </li>
                <li>
                  <strong>Legal and regulatory bodies</strong> — we disclose data when
                  required by a court order or other legal obligation.
                </li>
              </ul>
            </Block>

            <Block title="Data outside the EU">
              <p>
                We store and process your data within the European Economic Area wherever
                possible. If a service provider operates outside the EEA, we ensure adequate
                protections are in place — typically through Standard Contractual Clauses
                approved by the European Commission.
              </p>
            </Block>

            <Block title="How we protect your data">
              <p>
                We use HTTPS/TLS encryption for all data in transit, enforce strict access
                controls, and carry out regular security reviews. If a breach occurs that
                poses a risk to your rights and freedoms, we will notify the Dutch Data
                Protection Authority (Autoriteit Persoonsgegevens) within 72 hours and
                inform affected users promptly.
              </p>
            </Block>

            <Block title="Your rights">
              <p>
                Under GDPR you have meaningful control over your personal data. You can:
              </p>
              <ul>
                <li>Request a copy of the data we hold about you</li>
                <li>Ask us to correct inaccurate information</li>
                <li>Ask us to delete your data (where no legal obligation requires us to keep it)</li>
                <li>Ask us to restrict or stop processing your data</li>
                <li>Object to processing based on legitimate interests, including marketing</li>
                <li>Withdraw any consent you have given at any time</li>
              </ul>
              <p>
                To exercise any of these rights, email us at{' '}
                <a href="mailto:admin@buydeal.eu">admin@buydeal.eu</a>. We will respond
                within 30 days and may ask for proof of identity to protect your account.
              </p>
            </Block>

            <Block title="Children">
              <p>
                Buydeal is not directed at children under the age of 16. We do not knowingly
                collect personal data from minors. If you believe a child has submitted
                personal data through our platform, please contact us and we will remove it.
              </p>
            </Block>

            <Block title="Changes to this policy">
              <p>
                We may update this policy as our service evolves or as legal requirements
                change. If we make a significant change, we'll let registered users know by
                email. The date at the top of this page shows when the policy was last revised.
              </p>
            </Block>
			
			<Block title="Accept Privacy Policy">
				<p>
				The Privacy Policy is part of the General Terms and Conditions of Buydeal.eu. 
				By placing an order on the website from Buydeal.eu you automatically agree with the Privacy Policy and 
				General Terms and Conditions of Buydeal.eu. If you do not want to agree with this Privacy Policy or our 
				Terms and Conditions we ask you not to leave any information with us and do not place an order (s).
				</p>
			</Block>

            <Block title="Get in touch">
              <p>
                For any privacy-related questions:<br />
                Email: <a href="mailto:admin@buydeal.eu">admin@buydeal.eu</a><br />
                Address: Utrecht, Netherlands
              </p>
            </Block>

          </div>
        )}

        {/* ── COOKIES ── */}
        {activeTab === 'cookies' && (
          <div className="privacy-content">

            <div className="privacy-intro-box">
              Cookies are small files stored on your device that help websites work properly
              and remember your preferences. This page explains which cookies Buydeal uses,
              why, and how you can control them.
            </div>

            <Block title="What we use cookies for">
              <p>We use cookies on buydeal.eu to:</p>
              <ul>
                <li>Keep you logged in and maintain your session securely</li>
                <li>Remember your language and display preferences</li>
                <li>Understand how visitors use our site so we can improve it</li>
              </ul>
              <p>
                When you first visit the site, we ask for your consent to non-essential
                cookies. You can change your mind at any time through your browser settings.
              </p>
            </Block>

            <Block title="Essential cookies">
              <p>
                These are always active because the site cannot work without them. They
                manage your session, remember your login state, and protect against
                cross-site request forgery.
              </p>
              <div className="privacy-cookie-table-wrap">
                <table className="privacy-table">
                  <thead>
                    <tr><th>Cookie</th><th>What it does</th><th>Expires</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>session_id</td>
                      <td>Keeps you logged in during your visit</td>
                      <td>End of session</td>
                    </tr>
                    <tr>
                      <td>lang_pref</td>
                      <td>Saves your language preference</td>
                      <td>1 year</td>
                    </tr>
                    <tr>
                      <td>csrf_token</td>
                      <td>Protects forms against cross-site attacks</td>
                      <td>End of session</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Block>

            <Block title="Analytics cookies">
              <p>
                With your consent, we use Google Analytics to measure how the site is used.
                All data is anonymised — it cannot be linked back to you personally. This
                helps us understand which pages are most useful and where we can improve.
              </p>
              <div className="privacy-cookie-table-wrap">
                <table className="privacy-table">
                  <thead>
                    <tr><th>Cookie</th><th>What it does</th><th>Expires</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>_ga</td>
                      <td>Identifies unique visitors (anonymised)</td>
                      <td>2 years</td>
                    </tr>
                    <tr>
                      <td>_gid</td>
                      <td>Tracks sessions within a 24-hour window</td>
                      <td>24 hours</td>
                    </tr>
                    <tr>
                      <td>_gat</td>
                      <td>Limits the rate of analytics requests</td>
                      <td>1 minute</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>
                <a href="https://business.safety.google/privacy/" target="_blank" rel="noreferrer">
                  Read how Google uses this data →
                </a>
              </p>
            </Block>

            {/*<Block title="Social media cookies">
              <p>
                Our site includes share buttons for LinkedIn, Instagram and Facebook. If you click one,
                that platform may place its own cookies to recognise your account. These
                cookies are controlled by the respective platforms — please review their
                privacy policies for full details.
              </p>
              <div className="privacy-cookie-table-wrap">
                <table className="privacy-table">
                  <thead>
                    <tr><th>Provider</th><th>Purpose</th><th>Expires</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Facebook / Instagram/ Meta</td>
                      <td>Enables sharing and may recognise logged-in Facebook users</td>
                      <td>Up to 90 days</td>
                    </tr>
                    <tr>
                      <td>LinkedIn</td>
                      <td>Enables sharing on LinkedIn</td>
                      <td>Up to 2 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Block>*/}

            <Block title="Managing your cookie preferences">
              <p>
                You can control cookies directly in your browser. Most browsers let you
                block all cookies, block only third-party cookies, or delete cookies that
                have already been stored. Keep in mind that blocking essential cookies may
                prevent parts of the site from working correctly.
              </p>
              <p>Quick links for the most common browsers:</p>
              <ul>
                <li>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noreferrer">Google Chrome</a>
                </li>
                <li>
                  <a href="https://support.mozilla.org/nl/kb/cookies-informatie-websites-computer-opgeslagen" target="_blank" rel="noreferrer">Mozilla Firefox</a>
                </li>
                <li>
                  <a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noreferrer">Safari</a>
                </li>
                <li>
                  <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noreferrer">Microsoft Edge</a>
                </li>
              </ul>
              <p>
                To opt out of specific advertising or tracking cookies across sites, visit{' '}
                <a href="http://www.youronlinechoices.eu/" target="_blank" rel="noreferrer">
                  Your Online Choices
                </a>.
              </p>
            </Block>

            {/*<Block title="Third-party cookies we may not control">
              <p>
                Occasionally, third-party content embedded on our pages (such as maps or
                videos) may set their own cookies. We aim to list all known cookies above,
                but if you spot one we've missed, please email{' '}
                <a href="mailto:admin@buydeal.eu">admin@buydeal.eu</a> and we'll
                investigate.
              </p>
            </Block>*/}

            <Block title="Updates to this cookie policy">
              <p>
                We review this page whenever our cookie usage changes. The "last updated"
                date at the top of this page always reflects the current version. No advance
                notice is given for minor clarifications, but significant changes affecting
                your consent will be communicated via a new cookie banner.
              </p>
            </Block>
			
			<Block title="Accept Privacy Policy">
				<p>
				The Privacy Policy is part of the General Terms and Conditions of Buydeal.eu. 
				By placing an order on the website from Buydeal.eu you automatically agree with the Privacy Policy and 
				General Terms and Conditions of Buydeal.eu. If you do not want to agree with this Privacy Policy or our 
				Terms and Conditions we ask you not to leave any information with us and do not place an order (s).
				</p>
			</Block>
          </div>
        )}

      </div>

      {/* FOOTER */}
      <footer className="privacy-footer">
	  <div className="footer-bottom">
        <p>
          © {new Date().getFullYear()} Buydeal.eu B.V. All Rights Reserved.        </p>
		  <div className="footer-legal">
		    <a href="/privacy">Privacy Policy</a>
		    <a href="/toc">Terms &amp; Conditions (Buyers)</a>
		    <a href="/toc?tab=organisers">Terms &amp; Conditions (Organisers)</a>
		  </div>

		</div>
      </footer>

    </div>
  );
};

const Block = ({ title, children }) => (
  <div className="privacy-block">
    <h2>{title}</h2>
    {children}
  </div>
);

export default PrivacyPolicy;
